import type { ReactNode } from 'react';
import { cn } from '../ui/primitives';

/**
 * One offer batch: everything it is built from in a column, with what it totals
 * pinned beside it.
 *
 * An offer is a grid of possibilities, and the summary is the answer to the
 * question every pick above it asks — "what does this come to?". Stacked at the
 * foot of the batch it was a scroll away from every control that changes it, so
 * reading the answer meant losing sight of the question. It rides along instead.
 *
 * Presentational, and deliberately not the offer's own file: the admin batch and
 * the client batch are two renderings of the same build, so the arrangement is
 * shared even while only one of them uses it.
 *
 * ── Where it sticks ──
 *
 * Under the quote header, whatever height that is today: it collapses its
 * subtitle on scroll, wraps onto a second row on a narrow window, and carries
 * whichever actions the page gave it. `QuoteHeader` measures itself into
 * `--quote-header-h` for exactly this — a fixed offset guessed from one of those
 * states puts the panel's own heading behind the header in the others, which is
 * how this started. The fallback is the collapsed height, the state a rail deep
 * enough in the page to be pinned is almost always looking at.
 *
 * The rail does NOT scroll inside itself. It could — a summary with enough
 * products, fees and discounts outgrows the space under the header — but a
 * nested scroller takes the wheel first whenever the pointer is over it, which
 * scrolls the panel's heading out of view and leaves it there. Sticky already
 * has an answer that costs nothing: the rail unpins when the batch beside it
 * ends, so the tail of an over-tall summary is reached by scrolling to the foot
 * of the offer, exactly where it used to live.
 *
 * ── Where it splits, and why it is a container query ──
 *
 * Below the split the summary goes back under the column. The rule is the one
 * this layout has always claimed: a third column that squeezes the batch's
 * tables into permanent horizontal scroll costs more than the stacking it
 * saves. What was wrong was the number and what it measured.
 *
 * It measured the VIEWPORT, at `xl`. But what the tables get is this layout's
 * own width — the viewport less the app's 236px sidebar, less the page's
 * gutters, capped at the page's content maximum — so on an ordinary 1280px
 * window the rail appeared with ~608px left for a table that wants 829, and the
 * figures in it ran into each other. A container query asks the question the
 * tables actually care about, and keeps asking it correctly if the shell around
 * the page ever changes width.
 *
 * ── Why the rail is elastic, and why that is the whole trick ──
 *
 * A fixed rail is what forced the choice between the two. Held at its maximum,
 * the admin's tier table only clears its 829 from about 1200px of container —
 * which is a ~1500px WINDOW once the sidebar and gutters are paid for, so every
 * 13" laptop lost the rail. Yet the rail does not need its maximum to do its
 * job: the panel inside it is a column of label-and-figure rows that reads fine
 * at 280, and it already drops to one column well above that.
 *
 * So the rail spends what is spare and no more — `clamp(280px, 26%, 420px)`. At
 * a 1440px window (container 1140) it takes close to its 280 floor and leaves
 * ~850 for the batch: the rail stays AND the tier table fits, which at a fixed
 * maximum could not both be true. Past ~1077px of container the percentage
 * overtakes the floor and the rail grows, reaching 420 at ~1615.
 *
 * ── Why 26% and 420, not 22% and 360 ──
 *
 * The summary READS at 280; it does not read well there. It carries the widest
 * label-and-figure pairs on the page — "Products subtotal", "Sample delivery",
 * a six-figure total with its currency — and at the old 360 cap those rows ran
 * label-into-figure on a wide screen, where there was spare width going unspent.
 *
 * The floor is deliberately untouched at 280. It is the number the threshold
 * below is built from, so raising it would move the point where the rail
 * disappears entirely — a much worse trade than a snug summary. Only the slope
 * and the ceiling moved, which means nothing regresses: 26% exceeds the floor
 * from ~1077 of container upward, and below that the clamp returns 280 exactly
 * as it did before.
 *
 * ── The threshold ──
 *
 * 1020: the delivery table's 720 plus the 12px gap plus the rail's 280 floor.
 * Delivery is the constraint rather than the tier table because it is the widest
 * table EVERY batch carries — the tier table's own minimum depends on whether
 * the three economics columns are drawn (829 for the admin, 465 for a client),
 * so keying the split to it would give the two callers different thresholds for
 * a rail that is equally worth having in both.
 *
 * The consequence, and it is deliberate: between 1020 and ~1121 of container —
 * roughly a 1300–1420px window — the admin's tier table scrolls sideways behind
 * a rail that fits. That is the trade asked for. Scrolling a table whose columns
 * are legible costs little now that they have floors; it was only intolerable
 * back when a too-narrow column overlapped the one beside it instead.
 */
export function OfferBatchLayout({
	children,
	summary,
	className,
}: {
	/** The batch itself — header, plans, products, delivery, samples, extras. */
	children: ReactNode;
	/**
	 * What the picks total. Rendered in the rail, sticky once the batch and
	 * the rail both fit side by side — see the split above.
	 *
	 * Nothing when there is nothing to total — an offer with no products on it
	 * yet. The column is then the whole width rather than 360px of reserved
	 * emptiness beside it.
	 */
	summary?: ReactNode;
	className?: string;
}) {
	if (!summary) {
		return <div className={cn('flex min-w-0 flex-col gap-3', className)}>{children}</div>;
	}

	return (
		/* `self-start` on the rail is what makes it stick at all. A grid item's
		   sticky offsets are resolved inside its GRID AREA, and a stretched item
		   fills that area exactly — zero travel, so it never pins. Shrunk to its
		   content, the rest of the tall area beside the batch is its range. */
		<div className={cn('@container', className)}>
			{/* The 1020 and the 280 are the numbers reasoned about above, and they
			    are the same number twice: the threshold IS the delivery table's 720
			    plus the gap plus this floor. They live inline because a Tailwind
			    container variant and an arbitrary grid track are both literals the
			    compiler has to see — naming them as constants would only have moved
			    them, not joined them up. */}
			<div className="grid grid-cols-1 gap-3 @min-[1020px]:grid-cols-[minmax(0,1fr)_clamp(280px,26%,420px)]">
				<div className="flex min-w-0 flex-col gap-3">{children}</div>

				<div className="min-w-0 @min-[1020px]:sticky @min-[1020px]:top-[calc(var(--quote-header-h,56px)+16px)] @min-[1020px]:self-start">
					{summary}
				</div>
			</div>
		</div>
	);
}
